import React, { useState, useCallback, useEffect } from 'react';
import { Textarea } from '@mantine/core';

interface TextareaInputProps {
    onChange?: (value: string) => void;
    onSubmit?: () => void;
    disabled: boolean;
    value: string;
    placeholder?: string;
}

const TextareaInput = React.memo(({ onChange, onSubmit, value, disabled, placeholder = "Type your message..." }: TextareaInputProps) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        if (value?.trim() === "") {
            setLocalValue("");
        }
    }, [value]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);
        onChange?.(newValue);
    }, [onChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (localValue.trim()) {
                onSubmit?.();
                setLocalValue("");
            }
        }
    }, [onSubmit, localValue]);

    return (
        <Textarea
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            styles={{
                wrapper: {
                    display: 'flex',
                    flex: 1,
                },
                input: {
                    border: 'none',
                    background: 'transparent',
                    boxShadow: 'none',
                    outline: 'none',
                    resize: 'none',
                    padding: '20px 22px 14px',
                    fontSize: '16px',
                    lineHeight: '1.65',
                    color: '#1c1c1a',
                    fontFamily: "'Instrument Sans', sans-serif",
                    '&:focus': {
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                    },
                    '&:disabled': {
                        background: 'transparent',
                        color: '#a8a39a',
                        cursor: 'not-allowed',
                    },
                    '&::placeholder': {
                        color: '#918d86',
                        fontFamily: "'Instrument Sans', sans-serif",
                    },
                },
            }}
            className="chat-textarea"
            autosize
            minRows={2}
            maxRows={8}
        />
    );
});

export default TextareaInput;
